import React from "react";
import { Box, Divider, styled, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { LoadingButton } from "@rentablo/components";
import { RHFTextField } from "@rentablo/components";
import { preventNonNumericInput } from "@helpers";
import { Settings2FAContext } from "../stateMachine";

const QRCodeImage = styled("img")(({ theme }) => ({
  width: "140px",
  height: "140px",
  borderRadius: "8px",
  backgroundColor: "#FFF",
  border: `1px solid ${theme.palette.border.primary}`,
}));

interface Props {
  context: Settings2FAContext;
  onSubmit: (code: number) => void;
  isLoading?: boolean;
}

interface FormFields {
  code: number;
}

export const SetupView = ({ context, onSubmit, isLoading }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { control } = useForm<FormFields>({
    defaultValues: { code: undefined },
  });

  const code = useWatch({ control, name: "code" });

  const handleClick = () => {
    if (code) {
      onSubmit(code);
    }
  };

  return (
    <Box>
      <Typography variant="h6" mb={7}>
        {t("settings.twoFA.setup.step1.title")}
      </Typography>
      <Typography variant="body3" color="textSecondary" mb={7}>
        {t("settings.twoFA.setup.step1.subtitle")}
      </Typography>
      {/* QR code */}
      {context.qrCodeUrl && (
        <Box display="flex" justifyContent="center" alignItems="center">
          <QRCodeImage alt="qr-code" src={context.qrCodeUrl} />
        </Box>
      )}
      <Divider sx={{ my: 7.5, borderColor: theme.palette.border.secondary }} />
      <Typography variant="h6" mb={7}>
        {t("settings.twoFA.setup.step2.title")}
      </Typography>
      <Typography variant="body3" color="textSecondary" mb={7}>
        {t("settings.twoFA.setup.step2.subtitle")}
      </Typography>
      <RHFTextField
        label={t("settings.twoFA.codeLabel")}
        name="code"
        type="number"
        fullWidth
        control={control}
        sx={{ mb: 7 }}
        disabled={isLoading}
        testId="2fa-code-field"
        onBeforeInput={preventNonNumericInput}
      />
      <LoadingButton
        data-testid="2fa-setup-confirm-button"
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleClick}
        disabled={!code}
        isFetching={isLoading}
      >
        {t("settings.twoFA.setup.confirmButton")}
      </LoadingButton>
    </Box>
  );
};
